
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Conference } from '../../types/conference';
import { Order } from '../../types/order';
import { OrderRoom } from '../../types/orderRoom';

import { ConferenceHotelRoom, ConferenceHotelRoomResponse } from '../../types/conferenceHotelRoom';
import { APIHOST } from '../../common'; 
import { formatDateFromYYYYMMDD } from '../../../lib/datetime';

interface RoomSelectorProps {
  order: Order;
  setOrderProp?: (field: string, value: any) => void;
  setOrderUpdateCounter?: React.Dispatch<React.SetStateAction<number>>;
}

export interface RoomSelectorRef {
  saveAllRoomData: () => Promise<void>;
}

export const roomSelectorErrors = (order: Order) : string[] => {
    const errors: string[] = [];
    if (!order.order_rooms || order.order_rooms.length === 0) {
      errors.push('At least one room must be selected.');
      return errors;
    }

    order.order_rooms.forEach((room, index) => {
      if (room.conference_hotel_room === undefined) {
        errors.push(`Room ${index + 1} is missing a selected hotel room.`);
      }
      if (!room.arrivalDate) {
        errors.push(`Room ${index + 1} is missing an arrival date.`);
      }
      if (!room.departureDate) {
        errors.push(`Room ${index + 1} is missing a departure date.`);
      }
    })
    return errors;
  };

const RoomSelector = forwardRef<RoomSelectorRef, RoomSelectorProps>(({ order, setOrderProp, setOrderUpdateCounter }, ref) => {
  const [availableHotelRooms, setAvailableHotelRooms] = useState<ConferenceHotelRoom[]>([]);
  const [conferenceHotel, setConferenceHotel] = useState<any>(null);
  const [order_rooms, setOrderRooms] = useState<OrderRoom[]>(() => {
    // Use existing order rooms if available, otherwise start with empty array
    if (order.order_rooms && order.order_rooms.length > 0) {
      return order.order_rooms;
    }
    return [];
  });
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<OrderRoom | null>(null);
  const [dialogRoom, setDialogRoom] = useState<Partial<OrderRoom>>({});
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('error');

  // Sync local state with order prop when order changes
  useEffect(() => {
    if (order.order_rooms && order.order_rooms.length >= 0) {
      setOrderRooms(order.order_rooms);
    }
  }, [order.order_rooms]);

  // Reload order data after saving room
  const reloadOrderData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken || !order.documentId) return;

      const response = await fetch(
        `${APIHOST}/api/orders/${order.documentId}?populate[]=order_rooms.conference_hotel_room&populate[]=order_rooms&populate[]=conference_hotel&populate[]=conference_hotel.hotel`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        setOrderRooms(result.data.order_rooms || []);
        setOrderProp?.('order_rooms', result.data.order_rooms || []);
        setOrderUpdateCounter?.(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error reloading order data:', error);
    }
  };

  // Fetch available hotel rooms when conference hotel is selected
  useEffect(() => {
    const fetchHotelRooms = async () => {
      if (!order.conference_hotel) {
        setAvailableHotelRooms([]);
        return;
      }

      try {
        const authToken = localStorage.getItem('authToken');
        console.log('Fetching conference hotel rooms for hotel ID:', order.conference_hotel);
        const response = await fetch(
          `${APIHOST}/api/conference-hotel-rooms/available?orderId=${order.documentId}`,
          {
            headers: {
              'Authorization': `Bearer ${authToken}`,
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          const hotelRooms = data[0].conference_hotel_rooms || [];
          setAvailableHotelRooms(hotelRooms);
          setConferenceHotel(data[0].hotel);
        } else {
          console.error('Failed to fetch conference hotel');
          setAvailableHotelRooms([]);
          setConferenceHotel(null);
        }
      } catch (error) {
        console.error('Error fetching conference hotel:', error);
        setAvailableHotelRooms([]);
      }
    };

    fetchHotelRooms();
  }, [order.conference_hotel]);

  // Calculate number of days between arrival and departure
  const calculateDays = (arrivalDate: string, departureDate: string): number => {
    if (!arrivalDate || !departureDate) return 0;
    const arrival = new Date(arrivalDate);
    const departure = new Date(departureDate);
    const timeDiff = departure.getTime() - arrival.getTime();
    return Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
  };

  // Get daily rate for selected hotel room
  const getDailyRate = (conferenceHotelRoomId: string): number => {
    const hotelRoom = availableHotelRooms.find(room => room.documentId === conferenceHotelRoomId);
    return hotelRoom?.dailyRate || 0;
  };

  // Calculate subtotal for a room
  const calculateRoomSubtotal = (room: OrderRoom): number => {
    if (!room.conference_hotel_room || !room.arrivalDate || !room.departureDate) return 0;
    const days = calculateDays(room.arrivalDate, room.departureDate);
    const dailyRate = room.dailyRate;
    return days * dailyRate;
  }

  // Calculate total taxes for a room
  const calculateRoomTaxes = (room: OrderRoom): number => {
    if (!conferenceHotel) return 0;
    const subtotal = calculateRoomSubtotal(room);

  
    let totalTax = 0;
    
    // Add percentage-based tax
    if (conferenceHotel.taxRatePercentage) {
      totalTax += subtotal * (conferenceHotel.taxRatePercentage / 100);
    }
    
    // Add per-night tax
    if (conferenceHotel.taxPerRoomNight) {
      totalTax += calculateDays(room.arrivalDate, room.departureDate) * conferenceHotel.taxPerRoomNight;
    }
    
    return totalTax;
  };

  const calculateRoomTotal = (room: OrderRoom): number => {
      return calculateRoomTaxes(room) + calculateRoomSubtotal(room);
  }

  // API functions for room CRUD operations
  const saveRoomToAPI = async (room: OrderRoom, orderId: string) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    const { id, order_room_occupants, documentId, createdAt, updatedAt, publishedAt, ...roomData } = room;
    const payload = {
      data: {
        ...roomData
      }
    };

    try {
      const method = 'PUT';
      const url =`${APIHOST}/api/order-rooms/${room.documentId}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        return result.data;
      }
    } catch (error) {
      console.error('Error saving room:', error);
    }
    return null;
  };

  const addRoomToAPI = async (room: OrderRoom, orderId: string): Promise<OrderRoom | null> => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return null;

    const { id, order_room_occupants, documentId, createdAt, updatedAt, publishedAt, ...roomData } = room;
    const payload = {
      data: {
        ...roomData,
        order: orderId,
        conference_hotel_room: room.conference_hotel_room || null
      }
    };

    try {
      const response = await fetch(`${APIHOST}/api/order-rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        return result.data;
      }
    } catch (error) {
      console.error('Error adding room:', error);
    }
    return null;
  }

  const deleteRoomFromAPI = async (documentId: string) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken || !documentId) return;

    try {
      const response = await fetch(`${APIHOST}/api/order-rooms/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });
      return response.ok;
    } catch (error) {
      console.error('Error removing room:', error);
      return false;
    }
  };



  const handleRoomChange = (roomId: string, field: keyof OrderRoom, value: any) => {
    // Validate departure date is not before arrival date
    if (field === 'departureDate') {
      const room = order_rooms.find(r => r.id === roomId);
      if (room?.arrivalDate && value && new Date(value) <= new Date(room.arrivalDate)) {
        // Don't update if departure date is before or equal to arrival date
        return;
      }
    }
    
    // Validate arrival date is not after departure date
    if (field === 'arrivalDate') {
      const room = order_rooms.find(r => r.id === roomId);
      if (room?.departureDate && value && new Date(value) >= new Date(room.departureDate)) {
        // Don't update if arrival date is after or equal to departure date
        return;
      }
    }

    const updatedOrderRooms = order_rooms.map(room =>
      room.id === roomId ? { ...room, [field]: value } : room
    );
    setOrderRooms(updatedOrderRooms);
    setOrderProp?.('order_rooms', updatedOrderRooms);
  };

  // Dialog handlers
  const openAddRoomDialog = () => {
    setEditingRoom(null);
    setDialogRoom({
      type: 'student',
      arrivalDate: order.selectedArrivalDate || '',
      departureDate: order.selectedDepartureDate || '',
      holdStatus: 'held'
    });
    setDialogOpen(true);
  };

  const openEditRoomDialog = (room: OrderRoom) => {
    setEditingRoom(room);
    setDialogRoom({ ...room });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRoom(null);
    setDialogRoom({});
  };

  const handleDialogRoomChange = (field: keyof OrderRoom, value: any) => {
    // Validate departure date is not before arrival date
    if (field === 'departureDate') {
      if (dialogRoom.arrivalDate && value && new Date(value) <= new Date(dialogRoom.arrivalDate)) {
        return;
      }
    }
    
    // Validate arrival date is not after departure date
    if (field === 'arrivalDate') {
      if (dialogRoom.departureDate && value && new Date(value) >= new Date(dialogRoom.departureDate)) {
        return;
      }
    }

    setDialogRoom(prev => ({ ...prev, [field]: value }));
  };

  const saveRoomFromDialog = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const roomData = {
        ...dialogRoom,
        order: order.documentId
      };
      
      const { id, order_room_occupants, documentId, createdAt, updatedAt, publishedAt, ...cleanRoomData } = roomData;
      const payload = {
        data: cleanRoomData
      };

      const method = editingRoom?.documentId ? 'PUT' : 'POST';
      const url = editingRoom?.documentId 
        ? `${APIHOST}/api/order-rooms/${editingRoom.documentId}`
        : `${APIHOST}/api/order-rooms`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.status === 400) {
        const errorData = await response.json();
        console.error('Validation error saving room:', errorData.error.message);
        setSnackbarMessage(errorData.error.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      if (response.ok) {
        setSnackbarMessage('Room hold added');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        closeDialog();
        await reloadOrderData();
      } else {
        const errorData = await response.json();
        const errorMessage = errorData?.error?.message || 'Failed to save room';
        console.error('Failed to save room:', errorMessage);
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error saving room:', error);
    }
  };

  // Get hotel room name for display
  const getHotelRoomName = (conferenceHotelRoomId: string): string => {
    const hotelRoom = availableHotelRooms.find(room => room.documentId === conferenceHotelRoomId);
    return hotelRoom?.name || 'Unknown Room';
  };



  const addRoom = () => {
    openAddRoomDialog();
  };

  const removeRoom = async (roomId: string) => {
    const room = order_rooms.find(r => (r.documentId || r.id) === roomId);
    if (room && room.documentId) {
      const deleted = await deleteRoomFromAPI(room.documentId);
      if (deleted) {
        await reloadOrderData();
      } else {
        console.error('Failed to remove room from API');
      }
    }
  };



  // Save all room changes to API
  const saveAllRoomData = async () => {
    console.debug('All room data already saved to API');
    // Rooms are now saved individually through the dialog, so this is a no-op
  };

  // Expose saveAllRoomData function to parent component
  useImperativeHandle(ref, () => ({
    saveAllRoomData
  }));
console.debug('Rendering RoomSelector with order rooms:', availableHotelRooms);
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h3">
          Room Selection
        </Typography>
        <Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRoom}
          size="small"
        >
          Add Room
        </Button>
        </Box>
      </Box>


      {order_rooms.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No rooms selected yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Click "Add Room" to start selecting your accommodations
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addRoom}
          >
            Add Your First Room
          </Button>
        </Box>
      ) : (
        order_rooms.map((room, index) => (
          <Card key={room.documentId || room.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h4">
                  Room {index + 1}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={() => openEditRoomDialog(room)}
                    color="primary"
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => removeRoom(room.documentId || room.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Hotel Room
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {room.conference_hotel_room?.name}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Room Type
                  </Typography>
                  <Chip 
                    label={room.type || 'Student'} 
                    size="small" 
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Check-in
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {room.arrivalDate ? formatDateFromYYYYMMDD(room.arrivalDate) : 'Not set'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Check-out
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {room.departureDate ? formatDateFromYYYYMMDD(room.departureDate) : 'Not set'}
                  </Typography>
                </Grid>
              </Grid>

              {room.conference_hotel_room && room.arrivalDate && room.departureDate && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'success.50', borderRadius: 1, border: 1, borderColor: 'success.200' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {calculateDays(room.arrivalDate, room.departureDate)} nights × ${room.dailyRate}/night
                      ({`tax $${calculateRoomTaxes(room).toFixed(2)}`})
                    </Typography>
                    <Typography variant="h6" fontWeight="600" color="success.main">
                      ${calculateRoomTotal(room).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}
      
      {/* Add Room Button Below Rooms */}
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRoom}
          size="medium"
        >
          Add Another Room
        </Button>
      </Box>
      
      {/* Grand Total Display */}
      {order_rooms.some(room => room.conference_hotel_room && room.arrivalDate && room.departureDate) && (
        <Box sx={{ mt: 3}}>
         
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="600">
                Total Accommodation Cost
              </Typography>
              <Typography variant="h4" fontWeight="700" color="success.main">
                ${order_rooms.reduce((total, room) => total + calculateRoomTotal(room), 0).toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Total for {order_rooms.filter(room => room.conference_hotel_room).length} room(s)
            </Typography>
        
        </Box>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {roomSelectorErrors(order).length > 0 && (
          <Box sx={{ mt: 2, color: 'error.main' }}>
            {roomSelectorErrors(order).map((error, idx) => (
              <Typography key={idx} variant="body2">
                {error}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
      
      {/* Temporary button for testing saveAllRoomData */}
      {/* <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button 
          variant="outlined" 
          onClick={saveAllRoomData}
          color="secondary"
        >
          Save All Room Data (Temp)
        </Button>
      </Box> */}

      {/* Room Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRoom ? 'Edit Room' : 'Add Room'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Hotel Room</InputLabel>
        
                <Select
                  value={dialogRoom.conference_hotel_room || ''}
                  label="Hotel Room"
                  onChange={(e) => handleDialogRoomChange('conference_hotel_room', e.target.value)}
                  disabled={!order.conference_hotel || availableHotelRooms.length === 0}
                >
                  <MenuItem value="">
                    <em>Select a room</em>
                  </MenuItem>
                  {availableHotelRooms.map((hotelRoom) => (
                    <MenuItem key={hotelRoom.documentId} value={hotelRoom.documentId}>
                      {hotelRoom.name} - ${hotelRoom.dailyRate}/night
                      {hotelRoom.maxOccupants && ` (Max ${hotelRoom.maxOccupants})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Room Type</InputLabel>
                <Select
                  value={dialogRoom.type || 'student'}
                  label="Room Type"
                  onChange={(e) => handleDialogRoomChange('type', e.target.value)}
                >
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="chaperone">Chaperone</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Arrival Date"
                type="date"
                value={dialogRoom.arrivalDate || ''}
                onChange={(e) => handleDialogRoomChange('arrivalDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: order.selectedArrivalDate,
                  max: order.selectedDepartureDate
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Departure Date"
                type="date"
                value={dialogRoom.departureDate || ''}
                onChange={(e) => handleDialogRoomChange('departureDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: order.selectedArrivalDate,
                  max: order.selectedDepartureDate
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button 
            onClick={saveRoomFromDialog} 
            variant="contained"
            disabled={!dialogRoom.conference_hotel_room || !dialogRoom.arrivalDate || !dialogRoom.departureDate}
          >
            {editingRoom ? 'Update Room' : 'Add Room'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
});

RoomSelector.displayName = 'RoomSelector';
export default RoomSelector;